import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Avatar } from "@mui/material";
import userD from "../../../assets/customerAssets/user_D.jpeg";
import voteIcon from "../../../assets/customerAssets/freeVotesIcon.svg";
import VideoComoponent from "./VideoComoponent";
import UserVoteComponent from "./UserVoteComponent";

// ─── Count-up hook ──────────────────────────────────────────────────────────
function useCountUp(target, duration = 1100, delay = 0) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let rafId, startTime = null;
    const tid = setTimeout(() => {
      const step = (ts) => {
        if (!startTime) startTime = ts;
        const p = Math.min((ts - startTime) / duration, 1);
        setValue(Math.floor(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) rafId = requestAnimationFrame(step);
        else setValue(target);
      };
      rafId = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(tid); if (rafId) cancelAnimationFrame(rafId); };
  }, [target, duration, delay]);
  return value;
}

// ─── Animated progress bar ──────────────────────────────────────────────────
const AnimatedBar = ({ voteCount, active, delay }) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!active) return;
    const pct = Math.min((voteCount / 500) * 100, 100);
    const tid = setTimeout(() => setWidth(pct), delay + 150);
    return () => clearTimeout(tid);
  }, [active, voteCount, delay]);

  return (
    <Box sx={{
      width: "100%", height: "3px",
      bgcolor: "rgba(255,255,255,0.1)",
      borderRadius: "2px", overflow: "hidden", mt: "6px",
    }}>
      <Box sx={{
        height: "100%",
        width: `${width}%`,
        background: "linear-gradient(90deg, #d93349, #ff6b6b)",
        borderRadius: "2px",
        transition: "width 1.1s cubic-bezier(0.25,0.46,0.45,0.94)",
        boxShadow: "0 0 6px rgba(217,51,73,0.5)",
      }} />
    </Box>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────
const GoalVideoComponent = ({ data, setTournamentData, rank, index }) => {
  const rowRef = useRef(null);
  const [active,  setActive]  = useState(false);
  const [hovered, setHovered] = useState(false);

  const stagger = Math.min((index ?? (rank - 1)) * 55, 550);
  const isTop3  = rank <= 3;

  const animatedVotes = useCountUp(
    data?.voteCount || 0,
    1100,
    active ? stagger + 180 : 9999999
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          requestAnimationFrame(() => {
            setTimeout(() => setActive(true), stagger);
          });
          observer.disconnect();
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -20px 0px" }
    );
    if (rowRef.current) observer.observe(rowRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={rowRef}
      className="goal-video-outter"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        // ── Entry animation ──
        opacity:    active ? 1 : 0,
        transform:  active ? "translateY(0)" : "translateY(22px)",
        willChange: active ? "auto" : "opacity, transform",
        transition: "opacity 0.4s ease, transform 0.4s cubic-bezier(0.34,1.2,0.64,1), box-shadow 0.3s ease",

        // ── Hover ──
        position: "relative",
        cursor: "pointer",
        borderRadius: "8px",
        boxShadow: hovered
          ? isTop3
            ? "0 4px 20px rgba(255,215,0,0.12), 0 2px 8px rgba(0,0,0,0.3)"
            : "0 4px 20px rgba(217,51,73,0.12), 0 2px 8px rgba(0,0,0,0.3)"
          : "none",

        // Left accent bar
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0, top: "10%",
          width: "3px", height: "80%",
          borderRadius: "0 3px 3px 0",
          background: isTop3
            ? "linear-gradient(180deg, #FFD700, #FF8C00)"
            : "linear-gradient(180deg, #d93349, #ff6b6b)",
          transform: hovered ? "scaleY(1)" : "scaleY(0)",
          transformOrigin: "center",
          transition: "transform 0.25s cubic-bezier(0.34,1.3,0.64,1)",
          zIndex: 2,
        },

        // Background glow
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "8px",
          background: isTop3
            ? "linear-gradient(90deg, rgba(255,215,0,0.06) 0%, rgba(255,140,0,0.03) 100%)"
            : "linear-gradient(90deg, rgba(217,51,73,0.07) 0%, transparent 100%)",
          opacity: hovered ? 1 : 0,
          transition: "opacity 0.3s ease",
          pointerEvents: "none",
          zIndex: 1,
        },

        ...(active && {
          "&:hover": { transform: "translateY(-2px)" },
        }),
      }}
    >
      <Box
        className="goal-video-wrapper"
        sx={{
          position: "relative", zIndex: 2,
          "& .video-wrapper img, & .video-wrapper video": {
            transition: "transform 0.35s ease",
            transform: hovered ? "scale(1.04)" : "scale(1)",
          },
        }}
      >
        {/* Rank */}
        <Box
          className="goal-rank-div"
          sx={{
            transition: "color 0.25s ease",
            color: hovered && isTop3 ? "#FFD700 !important" : undefined,
          }}
        >
          {rank === 1 && <img src={voteIcon} width={40} height={40} alt="winner" />}
          {rank}
        </Box>

        {/* Thumbnail */}
        <Box className="video-wrapper" sx={{ overflow: "hidden", borderRadius: "6px" }}>
          <VideoComoponent
            img={data?.thumbnail}
            duration={data?.duration}
            data={data}
            setTournamentData={setTournamentData}
          />
        </Box>

        {/* Info */}
        <Box className="goal-info-wrapper">
          <Typography
            component="p"
            sx={{
              transition: "color 0.25s ease",
              color: hovered ? "#fff !important" : undefined,
            }}
          >
            Club: {data?.club_name}
          </Typography>
          <Typography component="p">
            Goal Scorer: {data?.goal_scorer}
          </Typography>
          <UserVoteComponent
            id={data.id}
            vote={data.isVoted}
            count={data?.voteCount}
            setTournamentData={setTournamentData}
          />

          {/* Animated progress bar */}
          <AnimatedBar voteCount={data?.voteCount || 0} active={active} delay={stagger} />
        </Box>

        {/* Avatar + animated vote count */}
        <Box
          className="goal-votes-wrapper"
          sx={{
            transition: "transform 0.3s ease",
            transform: hovered ? "scale(1.05)" : "scale(1)",
          }}
        >
          <Avatar
            alt="profile"
            src={data?.goal_image || data?.goalUserDetails?.profile_img || userD}
            sx={{
              width: 44, height: 44,
              transition: "box-shadow 0.3s ease",
              boxShadow: hovered
                ? isTop3
                  ? "0 0 0 2px #FFD700, 0 0 12px rgba(255,215,0,0.4)"
                  : "0 0 0 2px #d93349, 0 0 12px rgba(217,51,73,0.35)"
                : "none",
            }}
          />
          <Box className="votes-wrapper">
            <img src={voteIcon} height={20} width={20} alt="votes" />
            <Typography component="p">{animatedVotes} votes</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GoalVideoComponent;